/* eslint-disable react/jsx-props-no-spreading */
import React, { useMemo, ChangeEvent } from 'react';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import { SelectChangeEvent } from '@mui/material';
import TopBarTextField from './topbar-textfield';

const OTHER_VALUE = '__OTHER__';

interface IOption {
  value: string,
  label: string,
  disable?: boolean,
  disableText?: string,
  tooltipText?: string,
}

export interface IValue {
  hasOtherValue?:boolean;
  otherValues?: string | string[];
  selectedValues?: string | string[];
}

export interface Props {
  hidden?: boolean,
  value: IValue;
  disabled?: boolean;
  onChange: (newVal: IValue) => void;
  choices: IOption[];
  allowOther?: boolean;
  multiple: boolean;
  emptyLabel?: string;
  disableClearable?:boolean;
  label: string;
  topBarField?: boolean;
  startIcon?: (status: string) => React.ReactNode;
  isLoading?: boolean;
  showStatusIcon?: boolean;
  required?: boolean;
}

export const ChoiceField: React.FC<Props> = ({
  value,
  isLoading,
  disabled,
  onChange,
  choices,
  allowOther,
  multiple = false,
  emptyLabel,
  label,
  hidden = false,
  topBarField = false,
  startIcon,
  required = false,
  disableClearable = false,
}: Props) => {
  const { otherValues, selectedValues } = { ...value };

  const options = useMemo(() => {
    if (!choices) {
      return [];
    }
    return !emptyLabel ? [...choices] : [{ label: emptyLabel, value: '' }, ...choices];
  }, [choices]);

  const ensureArray = (values) => {
    if (!values) {
      return [];
    }
    return Array.isArray(values) ? values : [values];
  };

  const selected = useMemo(() => {
    const processedSelectedValues = ensureArray(selectedValues);
    const processedOtherValues = ensureArray(otherValues);

    const combinedValues = [...processedSelectedValues, ...processedOtherValues];

    if (combinedValues.length === 0) {
      return multiple ? [] : null;
    }

    // Map selected values and other values to options
    const found = combinedValues.map((val) => {
      const option = options.find((opts) => opts.value === val);
      return option || {
        label: val,
        value: `${OTHER_VALUE}_${val}`,
      };
    });

    // For single selection, return only one option or 'otherValue'
    if (!multiple) {
      return found[0] || null;
    }

    // For multiple selection, return all found options
    return found;
  }, [selectedValues, otherValues, multiple, options]);

  const handleMultipleSelection = (selectedVal: string | IOption | (string | IOption)[] | null) => {
    const array:Array<IOption|string> = ensureArray(selectedVal);
    const newOtherValues: string[] = [];
    const newSelectedValues: string[] = [];

    array.forEach((element) => {
      if (typeof element === 'string') {
        newOtherValues.push(element);
      } else if (element.value.includes(OTHER_VALUE)) {
        newOtherValues.push(element.label);
      } else if (element.value) {
        newSelectedValues.push(element.value);
      }
    });

    onChange({
      hasOtherValue: newOtherValues.length > 0,
      otherValues: newOtherValues,
      selectedValues: newSelectedValues,
    });
  };

  const handleSingleSelection = (selectedVal: IOption | string | null) => {
    let newOtherValue;
    let newSelectedValue;

    // Added a null check because we're now able to remove the selection
    if (!selectedVal) {
      onChange({
        hasOtherValue: newOtherValue !== undefined,
        otherValues: [],
        selectedValues: [],
      });

      return;
    }

    // This check is for custom added selections. It currently is not being used
    if (typeof selectedVal === 'string') {
      newOtherValue = selectedVal;
      // This check is for new "OTHER VALUE" selections. It also is not currently being used
    } else if (selectedVal.value.includes(OTHER_VALUE)) {
      newOtherValue = selectedVal.label;
    } else if (selectedVal.value) {
      newSelectedValue = selectedVal.value;
    }

    // This the status field expects a single object returned. Other selections do not
    // This is a workaround until we separate the status field from the choice field
    function handleStatusField(selection) {
      if (!selection) {
        return [];
      }

      if (label.includes('Status') || label === 'Country') {
        return selection;
      }

      return [selection];
    }

    onChange({
      hasOtherValue: newOtherValue !== undefined,
      otherValues: newOtherValue ? [newOtherValue] : [],
      selectedValues: handleStatusField(newSelectedValue),
    });
  };

  const handleTopBarChange = (event: SelectChangeEvent<unknown>) => {
    const topBarVal = { label: event.target.value, value: event.target.value };
    handleChange(null, topBarVal as IOption);
  };

  const handleChange = (
    event: ChangeEvent<{}> | null,
    selection: (string | IOption) | (string | IOption)[] | null,
  ) => {
    if (multiple) {
      handleMultipleSelection(selection);
    } else {
      handleSingleSelection(selection as IOption);
    }
  };

  const filterOptions = createFilterOptions<IOption>({
    matchFrom: 'any',
    limit: 1000,
    stringify: (option) => option.label,
  });

  const handleFilterOptions = (opts: IOption[], params: any) => {
    const filtered = filterOptions(opts, params);
    if (params.inputValue !== '') {
      const isExisting = opts.some((option) => option.label === params.inputValue);
      if (!isExisting) {
        filtered.push({
          label: params.inputValue,
          value: OTHER_VALUE,
        });
      }
    }
    return filtered;
  };

  const handleOptionLabel = (option) => option.label;

  return (
    <>
      {!hidden && (
        !topBarField ? (
          <Autocomplete
            multiple={multiple}
            options={options}
            freeSolo={allowOther}
            disableClearable={disableClearable}
            isOptionEqualToValue={(option, val) => option.value === val.value}
            value={selected}
            getOptionLabel={handleOptionLabel}
            disabled={disabled}
            onChange={handleChange}
            loading={isLoading}
            filterOptions={allowOther ? handleFilterOptions : undefined}
            renderOption={(props, option) => {
              const isCustomInput = option.value.includes(OTHER_VALUE);
              const optionLabel = isCustomInput ? `Add "${option.label}"` : option.label;
              return <li {...props} key={option.value} data-value={optionLabel}>{optionLabel}</li>;
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                required={required}
                label={label}
              />
            )}
          />
        ) : (
          <TopBarTextField
            select
            key={`choice-${options.length}`}
            value={(selected as IOption).value}
            SelectProps={{
              multiple,
              onChange: handleTopBarChange,
              MenuProps: {
                PaperProps: {
                  sx: { mt: 0.5 },
                },
              },
            }}
            required={required}
            InputProps={{
              ...(startIcon && !multiple) ? {
                startAdornment: (
                  <InputAdornment position="start">
                    {startIcon((selected as IOption).value.toLowerCase())}
                  </InputAdornment>
                ),
              } : {},
            }}
          >
            {options.map((option) => (
              <MenuItem
                key={option.value + option.label}
                value={option.value}
              >
                {option.label}
              </MenuItem>
            ))}
          </TopBarTextField>
        )
      )}
    </>
  );
};

export default ChoiceField;
